import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const PRIMARY_LINKS = [
  { name: "Home", href: "/" },
  { name: "Projects", href: "/work" },
  { name: "About", href: "/about" },
  { name: "Contact", href: "/contact" },
];

const SOCIAL_LINKS = [
  { name: "GitHub", href: "https://github.com/ahmed-dev8250" },
  { name: "LinkedIn", href: "https://www.linkedin.com/in/ahmadhamid03/" },
  { name: "YouTube", href: "https://www.youtube.com/@ThinkFlowAi" },
  { name: "Instagram", href: "https://www.instagram.com/shahmad_658/" },
  { name: "TikTok", href: "https://www.tiktok.com/@shnwabji2" },
];

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [onHero, setOnHero] = useState(true);

  useEffect(() => {
    const onScroll = () => setOnHero(window.scrollY < window.innerHeight * 0.8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const close = () => setIsOpen(false);
  const navigate = useNavigate();

  const handlePrimaryClick = (href: string) => {
    close();
    if (href.startsWith("/")) {
      navigate(href);
    }
  };

  /* text colour for the slim bar: dark on the light hero, white elsewhere */
  const barColor = onHero && !isOpen ? "text-[#181818]" : "text-white";

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* ── Full-screen overlay ── */}
      <div
        className={`fixed inset-0 bg-[#10100f] flex flex-col overflow-y-auto
          px-6 sm:px-8 md:px-16 lg:px-24 pt-20 sm:pt-24 md:pt-28 pb-8 md:pb-12
          ${isOpen ? "pointer-events-auto" : "pointer-events-none"}`}
        style={{
          clipPath: isOpen ? "inset(0 0 0% 0)" : "inset(0 0 100% 0)",
          transition: "clip-path 0.75s cubic-bezier(0.76, 0, 0.24, 1)",
        }}
      >
        {/* Primary links */}
        <nav className="flex min-h-0 flex-col">
          {PRIMARY_LINKS.map((link, i) => (
            <a
              key={link.name}
              href={link.href.startsWith("/") ? undefined : link.href}
              onClick={() => handlePrimaryClick(link.href)}
              className="group relative overflow-hidden border-b border-white/10 py-2.5 sm:py-3 md:py-5 cursor-pointer"
              style={{
                transform: isOpen ? "translateY(0)" : "translateY(2rem)",
                opacity: isOpen ? 1 : 0,
                transition: `transform 0.5s cubic-bezier(0.76,0,0.24,1) ${i * 70 + 150}ms,
                             opacity 0.5s ease ${i * 70 + 150}ms`,
              }}
            >
              {/* visible text */}
              <span
                className="block text-[clamp(3rem,11vh,6.75rem)] font-black
                               text-white uppercase tracking-tight leading-none
                               transition-transform duration-300 ease-out
                               group-hover:-translate-y-full"
              >
                {link.name}
              </span>
              {/* hover clone */}
              <span
                className="absolute inset-0 flex items-center
                               text-[clamp(3rem,11vh,6.75rem)] font-black
                               text-white/50 uppercase tracking-tight leading-none
                               translate-y-full transition-transform duration-300 ease-out
                               group-hover:translate-y-0"
              >
                {link.name}
              </span>
            </a>
          ))}
        </nav>

        {/* Divider */}
        <div className="h-px bg-white/15 my-4 md:my-6" />

        {/* Resume + Social links */}
        <div className="flex flex-wrap gap-x-5 gap-y-3 items-center pb-safe">
          <a
            href="/resume"
            onClick={close}
            className="group relative overflow-hidden text-xs uppercase tracking-widest text-white/80 border border-white/30 rounded-full px-5 py-2 hover:bg-white hover:text-black transition-colors duration-300"
            style={{
              opacity: isOpen ? 1 : 0,
              transition: `opacity 0.4s ease 300ms`,
            }}
          >
            View Resume
          </a>
          {SOCIAL_LINKS.map((link, i) => (
            <a
              key={link.name}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden text-xs uppercase tracking-widest text-white/40"
              style={{
                opacity: isOpen ? 1 : 0,
                transition: `opacity 0.4s ease ${i * 50 + 500}ms`,
              }}
            >
              <span className="block transition-transform duration-300 group-hover:-translate-y-full">
                {link.name}
              </span>
              <span
                className="absolute inset-0 text-white/80 translate-y-full
                               transition-transform duration-300 group-hover:translate-y-0"
              >
                {link.name}
              </span>
            </a>
          ))}
        </div>
      </div>

      {/* ── Slim nav bar ── */}
      <div className="relative z-10 flex items-center justify-between px-8 md:px-16 lg:px-24 py-6">
        {/* Logo */}
        <a
          href="/"
          className={`text-2xl font-black uppercase tracking-[0.2em]
            transition-colors duration-300 ${barColor}`}
        >
          MA
        </a>

        {/* Burger */}
        <button
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Toggle navigation"
          className={`flex flex-col gap-[5px] p-1 transition-colors duration-300 ${barColor}`}
        >
          <span
            className={`block h-[1.5px] w-6 bg-current origin-center
              transition-transform duration-300
              ${isOpen ? "rotate-45 translate-y-[3.25px]" : ""}`}
          />
          <span
            className={`block h-[1.5px] w-6 bg-current origin-center
              transition-transform duration-300
              ${isOpen ? "-rotate-45 -translate-y-[3.25px]" : ""}`}
          />
        </button>
      </div>
    </header>
  );
};

export default Navbar;
